import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MdKeyboardArrowDown, MdKeyboardArrowRight, MdOpenInNew } from 'react-icons/md';

// THE PRIMER READER — a whole research notebook (the `research-notebook-transfer-1.0` document)
// rendered as a reading edition.
//
// PORTED 1:1 FROM the GreenSkew reporting portal's PrimerReader (same notebook contract). Keep the
// two byte-identical below this header — fix the canonical one in GreenSkew, then mirror here.
//
// TWO NOTEBOOK STRUCTURES, ONE READER. Different companies arrive shaped differently, and BOTH
// must render fully:
//   - One section whose blocks carry an `eyebrow` chapter (e.g. Deere: Company Primer / Financial
//     Anatomy / …). Chapters come from the eyebrow.
//   - MANY sections, each its own chapter, blocks with no eyebrow (e.g. Vertiv: 18 sections).
//     Chapters come from the section title.
// We render EVERY section (not just the first) and take each block's chapter as its eyebrow, else
// its section title — so neither shape loses content.
//
// EVERY content shape is rendered, keyed off the fields present rather than a fixed type list, so
// a new block `type` cannot blank a section:
//   body → prose · columns+rows → table · nodes+edges → ordered flow ·
//   items[] → a list (strings OR objects) · readable_extra{} → a key/value table.
// A block with none of these still shows its title, and a generic sweep renders any leftover
// text/array/object field, so nothing in the document is silently dropped.

const slug = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
const isScalar = (v) => typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean';
const prettyKey = (k) => String(k).replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()).trim();

// Fields that are structure/metadata, never body content — used by the generic fallback so it
// only ever renders real content, never an id or a hash.
const META_FIELDS = new Set(['id', 'type', 'title', 'eyebrow', 'content_hash', 'source_refs',
  'related_source_refs', 'revision', 'status', 'method', 'summary']);

function paragraphs(body) {
  return String(body || '').split(/\n{2,}/).map((p) => p.trim()).filter(Boolean);
}

function orderChain(nodes, edges) {
  const list = Array.isArray(nodes) ? nodes : [];
  if (!list.length) return [];
  const byId = new Map(list.map((n) => [n.id, n]));
  const incoming = new Set((edges || []).map((e) => e.to));
  const nextOf = new Map();
  (edges || []).forEach((e) => { if (!nextOf.has(e.from)) nextOf.set(e.from, e.to); });
  const start = list.find((n) => !incoming.has(n.id)) || list[0];
  const order = []; const seen = new Set();
  let cur = start;
  while (cur && !seen.has(cur.id)) {
    order.push(cur); seen.add(cur.id);
    const nx = nextOf.get(cur.id);
    cur = nx ? byId.get(nx) : null;
  }
  list.forEach((n) => { if (!seen.has(n.id)) { order.push(n); seen.add(n.id); } });
  return order;
}

// ── value rendering (for items-of-objects and readable_extra) ─────────────────────────────
// A single value: scalar → text; array of scalars → comma-joined; array of objects → stacked
// KV groups; object → a KV table. Bounded by structure (objects/arrays only nest a couple of
// levels in the feed), so no explicit depth guard is needed.
function Val({ v }) {
  if (v == null || v === '') return <span className="text-gray-400">—</span>;
  if (Array.isArray(v)) {
    if (!v.length) return <span className="text-gray-400">—</span>;
    if (v.every(isScalar)) return <span>{v.join(', ')}</span>;
    return (
      <ul className="flex flex-col gap-1.5">
        {v.map((o, i) => <li key={i}>{isScalar(o) ? String(o) : <KV obj={o} />}</li>)}
      </ul>
    );
  }
  if (typeof v === 'object') return <KV obj={v} />;
  return <span>{String(v)}</span>;
}

// An object as a key → value table. Machine-ish keys (2025_products_pct) are prettified; values
// render through Val, so nested arrays/objects still resolve.
function KV({ obj }) {
  const entries = Object.entries(obj).filter(([, v]) => v != null && v !== '');
  if (!entries.length) return null;
  return (
    <dl className="grid grid-cols-[minmax(6rem,auto)_1fr] gap-x-3 gap-y-1 text-[13px]">
      {entries.map(([k, v]) => (
        <React.Fragment key={k}>
          <dt className="text-gray-500 dark:text-gray-400">{prettyKey(k)}</dt>
          <dd className="tabular-nums text-gray-800 dark:text-gray-200"><Val v={v} /></dd>
        </React.Fragment>
      ))}
    </dl>
  );
}

// items[] — a bulleted list. Items are strings (open_questions, key_takeaways, …) OR objects
// (a product-map entry, a timeline event); an object item renders as its own small KV group.
function ItemsBlock({ items }) {
  const list = Array.isArray(items) ? items.filter((x) => x != null && x !== '') : [];
  if (!list.length) return null;
  return (
    <ul className="flex flex-col gap-2">
      {list.map((it, i) => (
        <li key={i} className="flex gap-2">
          <span className="mt-[7px] h-1 w-1 shrink-0 rounded-full bg-brand-gold/70" aria-hidden="true" />
          <div className="min-w-0 flex-1 text-[13.5px] leading-relaxed text-gray-700 dark:text-gray-300">
            {isScalar(it) ? String(it) : <KV obj={it} />}
          </div>
        </li>
      ))}
    </ul>
  );
}

function TableBlock({ block }) {
  const cols = Array.isArray(block.columns) ? block.columns : [];
  const rows = Array.isArray(block.rows) ? block.rows : [];
  if (!cols.length || !rows.length) return null;
  const [head, ...rest] = cols;
  const cell = (v) => (v == null || v === '' ? '—' : String(v));

  return (
    <>
      <div className="flex flex-col gap-2.5 sm:hidden">
        {rows.map((r, i) => (
          <div key={i} className="rounded-lg border border-gray-200 p-3 dark:border-gray-800">
            <div className="mb-1.5 text-[13px] font-semibold text-gray-900 dark:text-gray-100">{cell(r[head.key])}</div>
            <dl className="flex flex-col gap-1.5">
              {rest.map((c) => (
                <div key={c.key}>
                  <dt className="text-[9.5px] font-medium uppercase tracking-[0.12em] text-gray-400">{c.label || c.key}</dt>
                  <dd className="text-[12.5px] leading-relaxed text-gray-700 dark:text-gray-300">{cell(r[c.key])}</dd>
                </div>
              ))}
            </dl>
          </div>
        ))}
      </div>
      <div className="scrollbar-thin hidden overflow-x-auto sm:block">
        <table className="w-full min-w-[36rem] border-collapse text-left text-[13px]">
          <thead>
            <tr className="border-b border-gray-200 dark:border-gray-700">
              {cols.map((c) => (
                <th key={c.key} className="px-3 py-2 align-bottom text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{c.label || c.key}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-b border-gray-100 align-top dark:border-gray-800/60">
                {cols.map((c) => <td key={c.key} className="px-3 py-2 text-gray-700 dark:text-gray-300">{cell(r[c.key])}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

// nodes+edges → ordered steps (process_chain AND causal_chain). Edge labels (causal_chain carries
// "why" on each edge) are shown between steps when present, so the causal reasoning is not lost.
function ChainBlock({ block }) {
  const steps = useMemo(() => orderChain(block.nodes, block.edges), [block.nodes, block.edges]);
  if (!steps.length) return null;
  return (
    <ol className="relative ml-1 border-l border-gray-200 dark:border-gray-700">
      {steps.map((n, i) => (
        <li key={n.id || i} className="relative pb-4 pl-5 last:pb-0">
          <span className="absolute -left-[9px] top-0.5 grid h-4 w-4 place-items-center rounded-full bg-brand-gold text-[9px] font-bold text-black">{i + 1}</span>
          <div className="text-[13px] font-semibold text-gray-800 dark:text-gray-200">{n.label}</div>
          {n.detail ? <div className="mt-0.5 text-[12.5px] leading-relaxed text-gray-600 dark:text-gray-400">{n.detail}</div> : null}
        </li>
      ))}
    </ol>
  );
}

function Block({ block }) {
  const paras = paragraphs(block.body);
  const hasTable = Array.isArray(block.columns) && Array.isArray(block.rows) && block.columns.length && block.rows.length;
  const hasChain = Array.isArray(block.nodes) && Array.isArray(block.edges) && block.nodes.length;
  const hasItems = Array.isArray(block.items) && block.items.length;
  const kv = block.readable_extra && typeof block.readable_extra === 'object' && !Array.isArray(block.readable_extra)
    ? block.readable_extra : null;
  const isCallout = block.type === 'callout';
  const rendered = paras.length || hasTable || hasChain || hasItems || kv;

  // Generic safety net: a future/unknown type with content in some other field still shows it,
  // rather than a bare title. Renders the first non-metadata array/object/long-string field.
  const fallback = !rendered
    ? Object.entries(block).find(([k, v]) => !META_FIELDS.has(k)
        && (Array.isArray(v) ? v.length : (v && typeof v === 'object') ? Object.keys(v).length : (typeof v === 'string' && v.length)))
    : null;

  return (
    <article className="scroll-mt-24">
      {block.title
        ? <h4 className="mb-2 font-serif text-[17px] leading-snug text-gray-900 dark:text-gray-100">{block.title}</h4>
        : null}
      {paras.length > 0 && (
        isCallout
          ? <div className="rounded-lg border-l-2 border-brand-gold/60 bg-brand-gold/5 px-3 py-2 text-[13px] italic leading-relaxed text-gray-600 dark:text-gray-400">
              {paras.map((p, i) => <p key={i} className="mb-1.5 last:mb-0">{p}</p>)}
            </div>
          : paras.map((p, i) => (
              <p key={i} className="mb-2.5 text-[14px] leading-relaxed text-gray-700 last:mb-0 dark:text-gray-300">{p}</p>
            ))
      )}
      {hasTable && <div className="mt-2"><TableBlock block={block} /></div>}
      {hasChain && <div className="mt-2"><ChainBlock block={block} /></div>}
      {hasItems && <div className="mt-2"><ItemsBlock items={block.items} /></div>}
      {kv && <div className="mt-2"><KV obj={kv} /></div>}
      {fallback && (
        <div className="mt-2">
          {isScalar(fallback[1]) ? <p className="text-[14px] leading-relaxed text-gray-700 dark:text-gray-300">{String(fallback[1])}</p>
            : Array.isArray(fallback[1]) ? <ItemsBlock items={fallback[1]} /> : <KV obj={fallback[1]} />}
        </div>
      )}
    </article>
  );
}

function Bibliography({ sources }) {
  const [open, setOpen] = useState(false);
  const groups = useMemo(() => {
    const m = new Map();
    (sources || []).forEach((s) => {
      const role = (s.details?.source_role || 'other');
      if (!m.has(role)) m.set(role, []);
      m.get(role).push(s);
    });
    return [...m.entries()].sort((a, b) => b[1].length - a[1].length);
  }, [sources]);
  if (!groups.length) return null;
  const total = (sources || []).length;
  return (
    <div className="mt-5 rounded-xl border border-gray-200 dark:border-gray-800">
      <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open}
              className="flex w-full items-center gap-2 px-4 py-2.5 text-left">
        <span className="grid w-5 place-items-center text-gray-400">
          {open ? <MdKeyboardArrowDown size={18} /> : <MdKeyboardArrowRight size={18} />}
        </span>
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Sources</span>
        <span className="text-[11px] text-gray-400">{total} references</span>
      </button>
      {open && (
        <div className="border-t border-gray-100 px-4 py-3 dark:border-gray-800/60">
          {groups.map(([role, items]) => (
            <div key={role} className="mb-4 last:mb-0">
              <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-400">
                {role.replace(/_/g, ' ')} · {items.length}
              </div>
              <ul className="flex flex-col gap-1.5">
                {items.map((s, i) => (
                  <li key={s.id || i} className="text-[12.5px] leading-snug">
                    {s.url
                      ? <a href={s.url} target="_blank" rel="noopener noreferrer"
                           className="inline-flex items-start gap-1 text-gray-600 hover:text-brand-gold dark:text-gray-400">
                          <span>{s.title || s.url}</span>
                          <MdOpenInNew size={12} className="mt-0.5 shrink-0 opacity-60" />
                        </a>
                      : <span className="text-gray-600 dark:text-gray-400">{s.title || '(untitled)'}</span>}
                    {s.details?.as_of_date ? <span className="ml-1.5 text-[10px] text-gray-400">{s.details.as_of_date}</span> : null}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function PrimerReader({ notebook }) {
  const pillRefs = useRef({});

  // Flatten EVERY section's blocks in order, tagging each with its chapter = the block's own
  // eyebrow (Deere-style) or, absent that, its section title (Vertiv-style). Then group in
  // first-seen order. This renders all 18 Vertiv sections, and still gives Deere its 4 eyebrow
  // chapters from a single section.
  const chapters = useMemo(() => {
    const secs = Array.isArray(notebook.sections) ? notebook.sections : [];
    const order = []; const map = new Map();
    secs.forEach((sec) => {
      (Array.isArray(sec.blocks) ? sec.blocks : []).forEach((b) => {
        const name = (b.eyebrow && String(b.eyebrow).trim()) || (sec.title && String(sec.title).trim()) || 'Notebook';
        if (!map.has(name)) { map.set(name, []); order.push(name); }
        map.get(name).push(b);
      });
    });
    return order.map((name) => ({ name, id: `ch-${slug(name)}`, blocks: map.get(name) }));
  }, [notebook]);

  const [active, setActive] = useState(chapters[0]?.id || null);

  const jump = (id) => {
    setActive(id);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  useEffect(() => {
    if (!chapters.length) return undefined;
    const obs = new IntersectionObserver((entries) => {
      const vis = entries.filter((e) => e.isIntersecting)
        .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
      if (vis[0]) setActive(vis[0].target.id);
    }, { rootMargin: '-20% 0px -70% 0px', threshold: 0 });
    chapters.forEach((c) => { const el = document.getElementById(c.id); if (el) obs.observe(el); });
    return () => obs.disconnect();
  }, [chapters]);

  // Keep the active pill centered in the strip WITHOUT ever calling scrollIntoView on it: the pill
  // lives in a position:sticky bar, and scrollIntoView on a sticky element scrolls the whole PAGE
  // back to that bar's in-flow position (the top) — the jump-to-top regression that surfaced once
  // every section rendered (the document got long enough for `active` to change mid-scroll). Scroll
  // only the strip, by the delta between the pill's centre and the strip's centre.
  useEffect(() => {
    const el = active && pillRefs.current[active];
    const strip = el && el.parentElement;
    if (!el || !strip) return;
    const s = strip.getBoundingClientRect();
    const e = el.getBoundingClientRect();
    const delta = (e.left + e.width / 2) - (s.left + s.width / 2);
    if (Math.abs(delta) > 1) strip.scrollBy({ left: delta, behavior: 'smooth' });
  }, [active]);

  if (!chapters.length) {
    return <p className="text-sm text-gray-400">This primer has no readable content.</p>;
  }

  return (
    <div>
      {/* Chapter pills — sticky. Labels truncate (some section titles are long) but the heading
          below shows the full title. */}
      <div className="sticky top-0 z-10 -mx-4 mb-4 border-b border-gray-100 bg-white/90 px-4 py-2 backdrop-blur dark:border-gray-800/60 dark:bg-[#161b22]/90">
        <div className="scrollbar-thin flex gap-2 overflow-x-auto">
          {chapters.map((c) => (
            <button key={c.id} type="button" onClick={() => jump(c.id)}
                    ref={(el) => { pillRefs.current[c.id] = el; }}
                    title={c.name}
                    className={`max-w-[13rem] shrink-0 truncate rounded-full border px-3 py-1 text-[12px] transition-colors ${active === c.id
                      ? 'border-brand-gold bg-brand-gold/10 text-brand-gold'
                      : 'border-gray-200 text-gray-600 hover:border-brand-gold dark:border-gray-700 dark:text-gray-300'}`}>
              {c.name}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-8">
        {chapters.map((c) => (
          <section key={c.id} id={c.id} className="scroll-mt-24">
            <div className="mb-3 flex items-start gap-2">
              <span className="mt-1.5 h-px w-5 shrink-0 bg-brand-gold/70" aria-hidden="true" />
              <h3 className="text-[11px] font-semibold uppercase tracking-[0.2em] text-brand-gold">{c.name}</h3>
            </div>
            <div className="flex flex-col gap-5">
              {c.blocks.map((b, i) => <Block key={b.id || i} block={b} />)}
            </div>
          </section>
        ))}
      </div>

      <Bibliography sources={notebook.sources} />
    </div>
  );
}
